import React, { useMemo } from 'react';
import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';

// Register Roboto fonts
Font.register({
  family: 'Roboto',
  fonts: [
    { 
      src: `${window.location.origin}/fonts/Roboto/Roboto-VariableFont_wdth,wght.ttf`,
      fontWeight: 'normal'
    },
    { 
      src: `${window.location.origin}/fonts/Roboto/Roboto-Italic-VariableFont_wdth,wght.ttf`,
      fontStyle: 'italic',
      fontWeight: 'normal'
    }
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontFamily: 'Roboto',
    fontSize: 8,
    position: 'relative',
    paddingBottom: 60
  },
  header: {
    marginBottom: 6,
    borderBottom: '1px solid #000',
    paddingBottom: 3
  },
  title: {
    fontSize: 16,
    fontWeight: 1200, // Extra bold
    marginBottom: 3,
    textAlign: 'center',
    textDecoration: 'underline',
    color: '#000'
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2
  },
  section: {
    marginBottom: 4
  },
  locationHeader: {
    fontSize: 14,
    fontWeight: 1200, // Extra bold
    marginBottom: 2,
    borderBottom: '1px solid #000',
    paddingBottom: 2,
    textAlign: 'center',
    color: '#000',
    textTransform: 'uppercase'
  },
  projectHeader: {
    fontSize: 12,
    fontWeight: 1600, // Extra bold
    marginBottom: 1,
    paddingTop: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#000',
    textTransform: 'uppercase'
  },
  projectName: {
    flex: 1,
    fontWeight: 1200 // Extra bold
  },
  categoryHeader: {
    fontSize: 8,
    fontWeight: 'bold',
    marginBottom: 1,
    fontStyle: 'italic',
    borderBottom: '1px solid #ddd',
    paddingBottom: 1
  },
  categoryName: {
    flex: 1,
  },
  indentInfo: {
    fontSize: 10,
    color: '#444',
    textAlign: 'right',
    marginLeft: 10,
    fontWeight: 'bold'
  },
  table: {
    marginBottom: 4
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingVertical: 2,
    alignItems: 'center'
  },
  tableHeader: {
    borderBottomWidth: 1,
    borderBottomColor: '#999',
    fontWeight: 'bold'
  },
  tableCell: {
    padding: 1,
    fontSize: 8,
    textAlign: 'left'
  },
  serialCell: {
    width: '5%'
  },
  itemNameCell: {
    width: '28%'
  },
  unitCell: {
    width: '8%',
    textAlign: 'right'
  },
  quantityCell: {
    width: '11%',
    textAlign: 'right'
  },
  stockCell: {
    width: '10%',
    textAlign: 'right'
  },
  dateCell: {
    width: '10%',
    textAlign: 'center'
  },
  descriptionCell: {
    width: '25%',
    textAlign: 'left'
  },
  grandTotalSection: {
    marginTop: 5,
    borderTop: '2px solid #000',
    paddingTop: 3
  },
  grandTotalRow: {
    flexDirection: 'row',
    paddingVertical: 2,
    justifyContent: 'space-between'
  },
  footerSection: {
    position: 'absolute',
    bottom: 30,
    left: 15,
    right: 15,
    textAlign: 'left',
    fontSize: 7,
    color: '#666',
    borderTop: '1px solid #ddd',
    paddingTop: 3,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  pageNumber: {
    fontSize: 8,
    color: '#666'
  },
  pageContent: {
    flex: 1,
    marginBottom: 45
  }
});

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
};

// Table header component
const TableHeader = () => (
  <View style={[styles.tableRow, styles.tableHeader]}>
    <Text style={[styles.tableCell, styles.serialCell]}>S.No</Text>
    <Text style={[styles.tableCell, styles.itemNameCell]}>Item Name</Text>
    <Text style={[styles.tableCell, styles.unitCell]}>Unit</Text>
    <Text style={[styles.tableCell, styles.quantityCell]}>Quantity</Text>
    <Text style={[styles.tableCell, styles.stockCell]}>Stock</Text>
    <Text style={[styles.tableCell, styles.dateCell]}>CD</Text>
    <Text style={[styles.tableCell, styles.descriptionCell]}>Remarks</Text>
  </View>
);

// Create a custom PageWrapper component that includes page numbers
const PageWrapper = ({ children }) => (
  <Page size="A4" style={styles.page}>
    <View style={styles.pageContent}>{children}</View>
    <View style={styles.footerSection} fixed>
      <Text>Generated on {formatDate(new Date())} • Indent Management System</Text>
      <Text
        style={styles.pageNumber}
        render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
      />
    </View>
  </Page>
);

// Sort items by ItemName with numbers-first priority, ascending (natural, case-insensitive)
const sortItemsByName = (items) => {
  const normalize = (s) => (s ?? '').toString().trim();
  const startsWithDigit = (s) => /^[0-9]/.test(s);
  return [...(items || [])].sort((a, b) => {
    const aName = normalize(a?.ItemName);
    const bName = normalize(b?.ItemName);
    const aNum = startsWithDigit(aName);
    const bNum = startsWithDigit(bName);
    if (aNum !== bNum) return aNum ? -1 : 1; // numbers first
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  });
};

const BatchPDF = ({ batch, data, selectedCategories = [] }) => {
  // Process the data to organize by location > project > items
  const processedData = useMemo(() => {
    if (!data || !selectedCategories || selectedCategories.length === 0) {
      return { locationData: {}, totalQuantity: 0 };
    }

    const locationData = {};
    let grandTotalQuantity = 0;
    
    // Group data by location first
    Object.entries(data).forEach(([location, projects]) => {
      if (!locationData[location]) {
        locationData[location] = {
          projects: {},
          totalQuantity: 0
        };
      }
      
      // Then by project within each location
      Object.entries(projects).forEach(([project, categories]) => {
        if (!locationData[location].projects[project]) {
          locationData[location].projects[project] = {
            items: [],
            totalQuantity: 0,
            indentNumbers: new Set() // Track unique indent numbers for this project
          };
        }
        
        // Collect all items from all categories and group by category
        Object.entries(categories).forEach(([category, items]) => {
          if (selectedCategories.includes(category)) {
            // Initialize categories object if it doesn't exist
            if (!locationData[location].projects[project].categories) {
              locationData[location].projects[project].categories = {};
            }
            
            // Store items by category (sorted)
            locationData[location].projects[project].categories[category] = sortItemsByName(items);
            
            // Track unique indent numbers
            items.forEach(item => {
              if (item.IndentNumber && item.IndentNumber !== 'N/A') {
                locationData[location].projects[project].indentNumbers.add(item.IndentNumber);
              }
            });
            
            // Calculate totals
            const categoryQuantity = items.reduce((sum, item) => sum + (Number(item.Quantity) || 0), 0);
            locationData[location].projects[project].totalQuantity += categoryQuantity;
            locationData[location].totalQuantity += categoryQuantity;
            grandTotalQuantity += categoryQuantity;
          }
        });
      });
    });
    
    return { locationData, grandTotalQuantity };
  }, [data, selectedCategories]);

  const { locationData, grandTotalQuantity } = processedData;

  if (!data || !selectedCategories || selectedCategories.length === 0) {
    return (
      <Document>
        <PageWrapper>
          <Text>No data available for export</Text>
        </PageWrapper>
      </Document>
    );
  }

  // Global serial number to be continuous across the entire document
  let globalSerialNumber = 1;

  return (
    <Document>
      <PageWrapper>
        <View style={styles.header}>
          <Text style={styles.title}>{batch.Description}</Text>
          <View style={styles.infoRow}>
            <Text>Batch Number: {batch.BatchNumber}</Text>
            <Text>Created At: {formatDate(batch.CreatedAt)}</Text>
          </View>
        </View>

        {/* Iterate through locations */}
        {Object.entries(locationData).map(([locationName, locationInfo], locationIndex) => (
          <View key={locationName} style={styles.section}>
            <View style={styles.locationHeader}>
              <Text>{locationName || 'Unassigned Location'}</Text>
            </View>
            
            {/* Iterate through projects within this location */}
            {Object.entries(locationInfo.projects).map(([projectName, projectInfo], projectIndex) => {
              return (
                <View key={`${locationName}-${projectName}`} style={{ marginBottom: 2 }}>
                  <View style={styles.projectHeader}>
                    <Text style={styles.projectName}>{projectName || 'Unassigned Project'}</Text>
                    {Array.from(projectInfo.indentNumbers).length > 0 && (
                      <Text style={styles.indentInfo}>
                        Indent No: {Array.from(projectInfo.indentNumbers).join(', ')}
                      </Text>
                    )}
                  </View>
                  {/* Show table header only once before the first category */}
                  {locationIndex === 0 && projectIndex === 0 && (
                    <View style={styles.table}>
                      <TableHeader />
                    </View>
                  )}
                  {/* Iterate through categories within this project */}
                  {Object.entries(projectInfo.categories || {}).map(([categoryName, categoryItems]) => (
                    <View key={categoryName} style={{ marginBottom: 3 }}>
                      {/*<View style={styles.categoryHeader}>
                        <Text style={styles.categoryName}>{categoryName}</Text>
                      </View>*/}
                      {/* Iterate through items in this category with continuous serial numbering */}
                      {categoryItems.map((item, index) => (
                        <View key={index} style={styles.tableRow}>
                          <Text style={[styles.tableCell, styles.serialCell]}>{globalSerialNumber++}</Text>
                          <Text style={[styles.tableCell, styles.itemNameCell]}>{item.ItemName}</Text>
                          <Text style={[styles.tableCell, styles.unitCell]}>{item.UnitName}</Text>
                          <Text style={[styles.tableCell, styles.quantityCell]}>{item.Quantity}</Text>
                          <Text style={[styles.tableCell, styles.stockCell]}>{item.Stock}</Text>
                          <Text style={[styles.tableCell, styles.dateCell]}>
                            {item.CommitedDays ? `${item.CommitedDays}` : 'N/A'}
                          </Text>
                          <Text style={[styles.tableCell, styles.descriptionCell]}>{item.Description || ''}</Text>
                        </View>
                      ))}
                    </View>
                  ))}
                  {/* Project subtotal row 
                  <View style={{ flexDirection: 'row', marginBottom: 6, marginTop: 2 }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold', textAlign: 'center', width: '100%' }}>
                      Total — {projectInfo.totalQuantity} MT
                    </Text>
                  </View>
                  */}
                </View>
              );
            })}
          </View>
        ))}
        {/* Grand total row at the end 
        <View style={styles.grandTotalSection}>
          <View style={styles.grandTotalRow}>
            <Text style={{ fontSize: 11, fontWeight: 'bold', textAlign: 'center', width: '100%' }}>
              Grand Total: {grandTotalQuantity} MT
            </Text>
          </View>
        </View>
        */}
      </PageWrapper>
    </Document>
  );
};

export default BatchPDF;